import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, Camera, X, AlertTriangle, Package, HelpCircle, Search, Eye, ExternalLink, ShieldCheck, Delete, Headphones, ArrowLeft, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import SurveyLandingPage from './SurveyLandingPage';
import BrandingShell from '../../components/shared/BrandingShell';
import { getCardSurface, hasWallpaper } from '../../components/shared/cardSurface';
import HighTechActionButton from '../../components/shared/HighTechActionButton';
import GlassInteractionPanel from '../../components/shared/GlassInteractionPanel';
import { getAccent } from '../../components/shared/semanticAccents';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/hotels-standalone`;
const INCIDENTS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/incidents`;

const CATEGORY_ICONS = {
  damaged_item: Package,
  spill_hazard: AlertTriangle,
  need_assistance: HelpCircle,
  product_missing: Search,
  suspicious_activity: Eye,
};

const CATEGORY_COLORS = {
  damaged_item: 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700',
  spill_hazard: 'border-red-400 bg-red-50 text-red-700',
  need_assistance: 'border-blue-400 bg-blue-50 text-blue-700',
  product_missing: 'border-fuchsia-400 bg-fuchsia-50/60 text-fuchsia-800',
  suspicious_activity: 'border-purple-400 bg-purple-50 text-purple-700',
};

const CATEGORY_SELECTED = {
  damaged_item: 'border-fuchsia-500 bg-fuchsia-100 ring-2 ring-fuchsia-400',
  spill_hazard: 'border-red-500 bg-red-100 ring-2 ring-red-400',
  need_assistance: 'border-blue-500 bg-blue-100 ring-2 ring-blue-400',
  product_missing: 'border-fuchsia-500 bg-fuchsia-100 ring-2 ring-fuchsia-400',
  suspicious_activity: 'border-purple-500 bg-purple-100 ring-2 ring-purple-400',
};

// Trilingual helper
const T = (lang, en, fr, es) => {
  if (lang === 'fr') return fr;
  if (lang === 'es') return es;
  return en;
};

// ============ PIN KEYPAD COMPONENT ============
function PinKeypad({ value, onChange, onSubmit, maxLength = 4, disabled = false }) {
  const keys = ['1','2','3','4','5','6','7','8','9','⌫','0','OK'];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto" data-testid="pin-keypad">
        {keys.map((key) => {
          if (key === '⌫') {
            return (
              <button key={key}
                disabled={disabled || !value}
                onClick={() => onChange(value.slice(0, -1))}
                className="h-14 rounded-xl bg-gray-100/10 hover:bg-gray-100/20 text-white/70 font-semibold text-lg transition-colors flex items-center justify-center disabled:opacity-30"
                data-testid="pin-key-backspace">
                <Delete className="w-5 h-5" />
              </button>
            );
          }
          if (key === 'OK') {
            return (
              <button key={key}
                disabled={disabled || !value}
                onClick={() => { if (value && onSubmit) onSubmit(); }}
                className="h-14 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:bg-white/5 disabled:text-white/20 text-white font-bold text-lg transition-colors"
                data-testid="pin-key-ok">
                OK
              </button>
            );
          }
          return (
            <button key={key}
              disabled={disabled || value.length >= maxLength}
              onClick={() => { if (value.length < maxLength) onChange(value + key); }}
              className="h-14 rounded-xl bg-white/5 border border-white/10 hover:bg-amber-500/15 hover:border-amber-400/30 text-white font-semibold text-xl transition-colors disabled:opacity-30"
              data-testid={`pin-key-${key}`}>
              {key}
            </button>
          );
        })}
      </div>
      {/* Clear All row */}
      <div className="max-w-[280px] mx-auto">
        <button
          disabled={disabled || !value}
          onClick={() => onChange('')}
          className="w-full h-9 rounded-lg text-xs text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors disabled:opacity-20"
          data-testid="pin-key-clear-all">
          Clear
        </button>
      </div>
    </div>
  );
}

export default function RetailerAlertSurvey() {
  const { orgId, locationId } = useParams();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('card');
  const langParam = searchParams.get('lang');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // steps: categories | photo_prompt | details | submitting | submitted
  // resolve steps: resolve_pin | resolve_confirm | resolve_photo | resolve_submitting | resolve_done
  const [step, setStep] = useState('categories');
  const [textComment, setTextComment] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState(langParam || 'en');
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [languageSettings, setLanguageSettings] = useState(null);
  const [reviewRedirect, setReviewRedirect] = useState(null);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Pyramid 2.1 state
  const [activeHazard, setActiveHazard] = useState(null);
  const [pinCode, setPinCode] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLocked, setPinLocked] = useState(false);
  const [pinLockSeconds, setPinLockSeconds] = useState(0);
  const [showForgotCode, setShowForgotCode] = useState(false);
  const [validatedUser, setValidatedUser] = useState(null);
  const [resolvePhoto, setResolvePhoto] = useState(null);
  const [resolveResult, setResolveResult] = useState(null);
  const [pinShake, setPinShake] = useState(false);
  const resolvePhotoRef = useRef(null);

  useEffect(() => {
    fetchSurveyData();
  }, [orgId, locationId]);

  const fetchSurveyData = async () => {
    try {
      const response = await fetch(`${API_URL}/retailer-alert-survey/${orgId}/${locationId}?card=${cardId || ''}&lang=${lang}`);
      if (!response.ok) throw new Error('Unable to load reporting interface');
      const data = await response.json();
      setSurveyData(data);

      // Handle language settings
      const ls = data.language_settings || {};
      setLanguageSettings(ls);
      const defaultLang = ls.default_lang || 'en';
      if (!langParam) setLang(defaultLang);
      if (!langParam && ls.landing_page_enabled) setShowLandingPage(true);
      if (data.review_redirect) setReviewRedirect(data.review_redirect);

      // Pyramid 2.1: Store active hazard data (do NOT auto-route — show categories first)
      if (data.active_hazard) {
        setActiveHazard(data.active_hazard);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 800;
          let w = img.width, h = img.height;
          if (w > MAX_SIZE || h > MAX_SIZE) {
            if (w > h) { h = (h / w) * MAX_SIZE; w = MAX_SIZE; }
            else { w = (w / h) * MAX_SIZE; h = MAX_SIZE; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.6));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await compressImage(file);
    setPhoto({ dataUrl, name: file.name });
  };

  const handleResolvePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await compressImage(file);
    setResolvePhoto({ dataUrl, name: file.name });
  };

  // ============ STANDARD REPORT SUBMISSION ============
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const category = surveyData.categories.find(c => c.id === selectedCategory);
      const payload = {
        card_id: cardId,
        category_id: selectedCategory,
        category_name: category ? (typeof category.name === 'object' ? category.name[lang] || category.name.en : category.name) : selectedCategory,
        text_comment: textComment.trim(),
      };
      if (photo?.dataUrl) payload.photo = photo.dataUrl;
      const response = await fetch(`${API_URL}/retailer-alert-survey/${orgId}/${locationId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Error submitting report');
      }
      if (reviewRedirect?.enabled && reviewRedirect?.url) {
        setShowReviewPrompt(true);
      } else {
        setStep('submitted');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ PYRAMID 2.1: CODE VALIDATION ============
  const handleValidateCode = useCallback(async (codeOverride) => {
    const codeToValidate = codeOverride || pinCode;
    if (!codeToValidate.trim() || pinLocked) return;
    setPinError('');
    try {
      const res = await fetch(`${INCIDENTS_URL}/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: codeToValidate,
          organization_id: orgId,
          incident_id: activeHazard?.incident_id || '',
        }),
      });
      if (res.status === 429) {
        const data = await res.json();
        const match = (data.detail || '').match(/(\d+)\s*seconds/);
        const secs = match ? parseInt(match[1]) : 300;
        setPinLocked(true);
        setPinLockSeconds(secs);
        setPinError(T(lang,
          `Too many attempts. Locked for ${Math.ceil(secs/60)} min.`,
          `Trop de tentatives. Verrouillé ${Math.ceil(secs/60)} min.`,
          `Demasiados intentos. Bloqueado ${Math.ceil(secs/60)} min.`
        ));
        setPinCode('');
        setPinShake(true);
        setTimeout(() => setPinShake(false), 500);
        // Auto-unlock countdown
        const timer = setInterval(() => {
          setPinLockSeconds(prev => {
            if (prev <= 1) { clearInterval(timer); setPinLocked(false); setPinError(''); return 0; }
            return prev - 1;
          });
        }, 1000);
        return;
      }
      if (!res.ok) {
        setPinError(T(lang,
          'Invalid code — please try again',
          'Code invalide — veuillez réessayer',
          'Código inválido — intente de nuevo'
        ));
        setPinCode('');
        setPinShake(true);
        setTimeout(() => setPinShake(false), 500);
        return;
      }
      const data = await res.json();
      setValidatedUser(data);
      setStep('resolve_confirm');
    } catch {
      setPinError(T(lang, 'Connection error', 'Erreur de connexion', 'Error de conexión'));
      setPinCode('');
      setPinShake(true);
      setTimeout(() => setPinShake(false), 500);
    }
  }, [pinCode, pinLocked, orgId, activeHazard, lang]);

  // Auto-validate when 4th digit is entered
  useEffect(() => {
    if (pinCode.length === 4 && step === 'resolve_pin' && !pinLocked) {
      handleValidateCode(pinCode);
    }
  }, [pinCode, step, pinLocked, handleValidateCode]);

  // ============ PYRAMID 2.1: RESOLVE SUBMISSION ============
  const handleResolveSubmit = async () => {
    if (!resolvePhoto?.dataUrl) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${INCIDENTS_URL}/${activeHazard.incident_id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: pinCode,
          organization_id: orgId,
          photo: resolvePhoto.dataUrl,
        }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Error resolving incident');
      }
      const data = await res.json();
      setResolveResult(data);
      setActiveHazard(null); // Reset so hazard button returns to normal red state
      setStep('resolve_done');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ LOGO COMPONENT ============
  const LogoDisplay = () => {
    if (!surveyData?.branding?.logo_url) return null;
    const logoUrl = surveyData.branding.logo_url.startsWith('http')
      ? surveyData.branding.logo_url
      : `${process.env.REACT_APP_BACKEND_URL}${surveyData.branding.logo_url}`;
    const sizeMap = { small: 'h-8', medium: 'h-12', large: 'h-16', very_large: 'h-20' };
    const sizeClass = sizeMap[surveyData.branding.logo_size] || 'h-12';
    return (
      <div className="flex justify-center mb-4">
        <img src={logoUrl} alt="Logo" className={`${sizeClass} object-contain`} />
      </div>
    );
  };

  // ============ BRANDING SHELL WRAPPER ============
  const brandingData = surveyData?.branding || {};
  const useWp = hasWallpaper(brandingData);
  const surfaceStyle = getCardSurface(brandingData.card_style);

  const PageShell = ({ children, fallbackGradient = 'from-fuchsia-600 to-fuchsia-800', cardClassName = '' }) => (
    <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient={fallbackGradient}>
      <Card className={`w-full max-w-md shadow-2xl rounded-xl ${useWp ? surfaceStyle.classes : 'bg-white'} ${cardClassName}`}>
        {children}
      </Card>
    </BrandingShell>
  );

  // ============ LANDING PAGE ============
  if (showLandingPage && surveyData) {
    return (
      <SurveyLandingPage
        branding={surveyData.branding}
        languageSettings={languageSettings}
        organizationName={surveyData.organization?.name || 'Store'}
        onSelectLanguage={(selectedLang) => { setLang(selectedLang); setShowLandingPage(false); }}
        apiBase={process.env.REACT_APP_BACKEND_URL}
      />
    );
  }

  // ============ LOADING ============
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" />
            <span className="ml-3 text-gray-600">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ ERROR ============
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 text-lg font-medium mb-2">{lang === 'fr' ? 'Erreur' : 'Error'}</p>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ================================================================
  // PYRAMID 2.1: RESOLVE HAZARD FLOW
  // ================================================================

  // Step: Enter PIN Code
  if (step === 'resolve_pin') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-6" data-testid="resolve-pin-screen">
            <LogoDisplay />

            {/* Status Confirmation */}
            <div className="text-center mb-5 bg-green-500/10 border border-green-500/30 rounded-xl p-4" data-testid="hazard-status-banner">
              <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-6 h-6 text-green-400" strokeWidth={1.5} />
              </div>
              <h1 className="text-base font-bold text-white mb-0.5">
                {T(lang, 'Hazard has been reported', 'Risque signalé', 'Peligro reportado')}
              </h1>
              <p className="text-sm text-green-400 font-medium">
                {T(lang, 'Staff have been notified', 'Le personnel a été notifié', 'El personal ha sido notificado')}
              </p>
              <div className="mt-2 flex items-center justify-center space-x-2">
                <p className="text-xs text-white/50">
                  {activeHazard?.location_label || surveyData?.location?.name}
                </p>
                <span className="text-xs text-amber-400 bg-amber-500/15 border border-amber-500/30 rounded px-2 py-0.5 font-medium">
                  {activeHazard?.category_name}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center mb-4">
              <div className="flex-1 border-t border-white/10" />
              <ShieldCheck className="w-4 h-4 mx-3 text-amber-400" strokeWidth={1.5} />
              <div className="flex-1 border-t border-white/10" />
            </div>

            <p className="text-center text-sm font-medium text-white/70 mb-3">
              {T(lang, 'Enter code to clear hazard', 'Entrez le code pour éliminer le risque', 'Ingrese el código para resolver')}
            </p>

            {/* PIN Display — 4 boxes */}
            <div className={`flex justify-center mb-4 ${pinShake ? 'animate-[shake_0.4s_ease-in-out]' : ''}`} data-testid="pin-display">
              <div className="flex gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i}
                    className={`w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all
                      ${pinLocked ? 'border-red-500/40 bg-red-500/10 text-red-300' :
                        i < pinCode.length ? 'border-amber-400 bg-amber-500/15 text-amber-300' :
                        'border-white/15 bg-white/5'}`}
                    data-testid={`pin-box-${i}`}>
                    {pinCode[i] ? '●' : ''}
                  </div>
                ))}
              </div>
            </div>

            {/* Shake animation keyframes */}
            <style>{`
              @keyframes shake {
                0%, 100% { transform: translateX(0); }
                20% { transform: translateX(-8px); }
                40% { transform: translateX(8px); }
                60% { transform: translateX(-5px); }
                80% { transform: translateX(5px); }
              }
            `}</style>

            {pinError && (
              <div className="flex items-center justify-center gap-2 mb-3" data-testid="pin-error">
                {pinLocked && <Lock className="w-3.5 h-3.5 text-red-400" />}
                <p className="text-center text-sm text-red-400 font-medium">{pinError}</p>
                {pinLocked && pinLockSeconds > 0 && (
                  <span className="text-xs text-red-400/70">({pinLockSeconds}s)</span>
                )}
              </div>
            )}

            <PinKeypad
              value={pinCode}
              onChange={(v) => { setPinCode(v); if (pinError && !pinLocked) setPinError(''); }}
              onSubmit={() => handleValidateCode()}
              maxLength={4}
              disabled={pinLocked}
            />

            <Button
              onClick={() => handleValidateCode()}
              disabled={!pinCode.trim() || pinLocked}
              className="w-full mt-4 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-400 font-semibold text-base hover:bg-amber-500/25 transition-all active:scale-[0.98] disabled:opacity-30"
              data-testid="validate-code-btn"
            >
              {T(lang, 'Validate Code', 'Valider le code', 'Validar código')}
            </Button>

            {/* Forgot Code */}
            {!showForgotCode ? (
              <button
                onClick={() => setShowForgotCode(true)}
                className="w-full mt-3 text-center text-xs text-white/30 hover:text-white/50 transition-colors"
                data-testid="forgot-code-link"
              >
                {T(lang, 'Forgot your code?', 'Code oublié?', '¿Olvidó su código?')}
              </button>
            ) : (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-center" data-testid="forgot-code-message">
                <p className="text-xs text-blue-300">
                  {T(lang,
                    'Ask your manager or supervisor for your code.',
                    'Demandez votre code à votre gestionnaire ou superviseur.',
                    'Solicite su código a su gerente o supervisor.'
                  )}
                </p>
              </div>
            )}

            {/* Back / Cancel */}
            <button
              onClick={() => { setStep('categories'); setPinCode(''); setPinError(''); setPinLocked(false); setShowForgotCode(false); }}
              className="w-full mt-3 h-11 rounded-2xl flex items-center justify-center gap-2 text-white/40 hover:text-white/60 hover:bg-white/5 transition-all active:scale-[0.98]"
              data-testid="back-from-pin-btn"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-medium">{T(lang, 'Back', 'Retour', 'Volver')}</span>
            </button>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Step: Hazard Cleared Confirmation
  if (step === 'resolve_confirm') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 pb-6" data-testid="resolve-confirm-screen">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-white/50 mb-1">
                {validatedUser?.first_name ? `${validatedUser.first_name} ${validatedUser.last_name || ''}`.trim() : ''}
              </p>
              <h1 className="text-xl font-bold text-white">
                {T(lang, 'Hazard Cleared?', 'Risque éliminé ?', '¿Peligro resuelto?')}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => { setStep('resolve_pin'); setPinCode(''); setValidatedUser(null); }}
                className="h-16 text-lg font-bold rounded-2xl bg-white/5 border border-white/15 text-white/70 hover:bg-white/10 transition-all active:scale-[0.98]"
                data-testid="hazard-not-cleared-btn"
              >
                {T(lang, 'NO', 'NON', 'NO')}
              </Button>
              <Button
                onClick={() => setStep('resolve_photo')}
                className="h-16 text-lg font-bold rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 hover:bg-green-500/25 transition-all active:scale-[0.98]"
                data-testid="hazard-cleared-btn"
              >
                {T(lang, 'YES', 'OUI', 'SÍ')}
              </Button>
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Step: Mandatory Photo Capture
  if (step === 'resolve_photo') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 pb-6" data-testid="resolve-photo-screen">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">
                {T(lang, 'Photo Required', 'Photo requise', 'Foto requerida')}
              </h1>
              <p className="text-sm text-white/50">
                {T(lang,
                  'Take a photo of the cleared area for verification.',
                  'Prenez une photo de la zone dégagée pour vérification.',
                  'Tome una foto del área despejada para verificación.'
                )}
              </p>
            </div>

            {resolvePhoto ? (
              <div className="relative mb-4">
                <img src={resolvePhoto.dataUrl} alt="Cleared area" className="w-full h-48 object-cover rounded-lg" />
                <button
                  onClick={() => setResolvePhoto(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                  data-testid="remove-resolve-photo-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => resolvePhotoRef.current?.click()}
                className="w-full h-40 border-2 border-dashed border-white/15 rounded-2xl flex flex-col items-center justify-center mb-4 hover:border-white/30 transition-colors"
                data-testid="capture-resolve-photo-btn"
              >
                <Camera className="w-10 h-10 text-white/40 mb-2" strokeWidth={1.5} />
                <span className="text-sm font-medium text-white/60">
                  {T(lang, 'Take Photo', 'Prendre une photo', 'Tomar foto')}
                </span>
              </button>
            )}
            <input
              ref={resolvePhotoRef}
              type="file"
              accept="image/*"
              onChange={handleResolvePhotoCapture}
              className="hidden"
              data-testid="resolve-photo-input"
            />

            <Button
              onClick={handleResolveSubmit}
              disabled={!resolvePhoto || submitting}
              className="w-full h-12 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 font-semibold text-base hover:bg-green-500/25 disabled:opacity-40 transition-all active:scale-[0.98]"
              data-testid="submit-resolve-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />{T(lang, 'Submitting...', 'Envoi...', 'Enviando...')}</>
              ) : (
                T(lang, 'Confirm Resolution', 'Confirmer la résolution', 'Confirmar resolución')
              )}
            </Button>

            <button
              onClick={() => setStep('resolve_confirm')}
              className="w-full mt-2 h-10 rounded-2xl text-white/40 hover:text-white/60 transition-all"
              data-testid="back-to-confirm-btn"
            >
              {T(lang, 'Back', 'Retour', 'Volver')}
            </button>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Step: Resolution Complete
  if (step === 'resolve_done') {
    const mins = resolveResult?.response_time_seconds ? Math.round(resolveResult.response_time_seconds / 60) : null;
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 text-center" data-testid="resolve-done-screen">
            <LogoDisplay />
            <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-green-400" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {T(lang, 'Hazard Cleared', 'Risque éliminé', 'Peligro resuelto')}
            </h1>
            <p className="text-white/50 mb-1">{activeHazard?.location_label}</p>
            {resolveResult?.resolved_by_name && (
              <p className="text-sm text-white/40 mb-1">
                {T(lang, 'By', 'Par', 'Por')}: {resolveResult.resolved_by_name}
              </p>
            )}
            {mins !== null && (
              <p className="text-sm text-emerald-400 font-medium">
                {T(lang, `Response time: ${mins} min`, `Temps de réponse: ${mins} min`, `Tiempo de respuesta: ${mins} min`)}
              </p>
            )}
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // ================================================================
  // STANDARD REPORTING FLOW
  // ================================================================

  // Review redirect prompt
  if (showReviewPrompt) {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 text-center" data-testid="review-redirect-prompt">
            <LogoDisplay />
            <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {lang === 'fr' ? 'Merci pour votre signalement!' : 'Thank you for your report!'}
            </h1>
            <p className="text-white/50 mb-6">
              {lang === 'fr'
                ? 'Pourriez-vous nous laisser un avis sur Google?'
                : 'Would you kindly leave us a review on Google?'}
            </p>
            <div className="space-y-3">
              <button
                className="w-full h-12 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 font-medium hover:bg-green-500/25 transition-all active:scale-[0.98] flex items-center justify-center"
                onClick={() => window.open(reviewRedirect.url, '_blank')}
                data-testid="leave-review-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {lang === 'fr' ? 'Laisser un avis' : 'Leave a Review'}
              </button>
              <button
                className="w-full h-10 rounded-2xl text-white/40 hover:text-white/60 transition-all"
                onClick={() => { setShowReviewPrompt(false); setStep('submitted'); }}
                data-testid="no-thanks-btn"
              >
                {lang === 'fr' ? 'Non, merci' : 'No, thanks'}
              </button>
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Submitted confirmation
  if (step === 'submitted') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 text-center" data-testid="retailer-alert-confirmation">
            <LogoDisplay />
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {lang === 'fr' ? 'Merci' : 'Thank you'}
            </h1>
            <p className="text-white/50">
              {lang === 'fr'
                ? 'Le personnel du magasin a \u00e9t\u00e9 notifi\u00e9.'
                : 'Store staff have been notified.'}
            </p>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  const categories = surveyData?.categories || [];
  const storeName = surveyData?.organization?.name || 'Store';

  // Step: Category Selection — HIGH-TECH INTERACTION UI
  if (step === 'categories') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        {/* Language Toggle */}
        <div className="fixed top-4 right-4 z-10">
          <button
            onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/80 text-sm hover:bg-white/15 transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            {lang === 'en' ? 'EN' : 'FR'}
          </button>
        </div>

        <GlassInteractionPanel>
          <div className="p-6 pb-8">
            <LogoDisplay />
            <div className="text-center mb-8">
              <h1 className="text-xl font-bold text-white mb-1">{storeName}</h1>
              <p className="text-sm text-white/50">
                {lang === 'fr' ? 'Comment pouvons-nous vous aider?' : 'How can we help?'}
              </p>
            </div>

            <div className="space-y-3" data-testid="category-list">
              {categories.map((cat) => {
                const IconComp = CATEGORY_ICONS[cat.id] || AlertTriangle;
                const accent = getAccent(cat.id);
                const isHazardActive = cat.id === 'spill_hazard' && activeHazard;

                if (isHazardActive) {
                  return (
                    <HighTechActionButton
                      key={cat.id}
                      icon={ShieldCheck}
                      title={lang === 'fr' ? 'D\u00e9j\u00e0 signal\u00e9 / Appuyez pour r\u00e9soudre' : 'Reported Already / Press to Clear'}
                      accent={{ color: '#f59e0b', glow: 'rgba(245,158,11,0.3)', border: 'rgba(245,158,11,0.5)', bg: 'rgba(245,158,11,0.1)' }}
                      onClick={() => setStep('resolve_pin')}
                      badge={lang === 'fr' ? 'ACTIF' : 'ACTIVE'}
                      testId={`category-${cat.id}`}
                    />
                  );
                }

                const catName = cat.id === 'spill_hazard' && !activeHazard
                  ? (lang === 'fr' ? 'Signaler un d\u00e9versement ou risque' : 'Report Spill or Safety Hazard')
                  : (typeof cat.name === 'object' ? (cat.name[lang] || cat.name.en) : cat.name);

                const parts = catName.split(' or ');
                const mainTitle = parts[0];
                const subText = parts.length > 1 ? parts.slice(1).join(' or ') : undefined;

                return (
                  <HighTechActionButton
                    key={cat.id}
                    icon={IconComp}
                    title={mainTitle}
                    description={subText}
                    accent={accent}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      if (cat.id === 'spill_hazard') {
                        setStep('photo_prompt');
                      } else {
                        setStep('details');
                      }
                    }}
                    testId={`category-${cat.id}`}
                  />
                );
              })}
            </div>
          </div>

          <div className="px-6 py-3 border-t border-white/5 flex items-center justify-center gap-2">
            <Headphones className="w-3.5 h-3.5 text-white/30" strokeWidth={1.5} />
            <span className="text-xs text-white/30">
              {lang === 'fr' ? 'Assistance du personnel. Nous sommes l\u00e0 pour vous aider.' : "Staff assistance. We're here to help."}
            </span>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Step: Photo Prompt (for Spill / Safety Hazard)
  if (step === 'photo_prompt') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-6 pb-8" data-testid="spill-photo-prompt">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-green-400" strokeWidth={1.5} />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">
                {lang === 'fr' ? 'Prendre une photo?' : 'Take a picture?'}
              </h1>
              <p className="text-sm text-white/50 px-4">
                {lang === 'fr'
                  ? 'Une photo nous aide \u00e0 \u00e9valuer la situation.'
                  : 'A photo helps us evaluate the situation.'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setStep('details')}
                className="h-14 rounded-2xl bg-white/5 border border-white/10 text-white/70 text-base font-medium hover:bg-white/10 transition-all active:scale-[0.98]"
                data-testid="photo-prompt-skip"
              >
                {lang === 'fr' ? 'Passer' : 'Skip'}
              </button>
              <button
                className="h-14 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 text-base font-medium hover:bg-green-500/25 transition-all active:scale-[0.98] flex items-center justify-center"
                data-testid="photo-prompt-capture"
                onClick={() => document.getElementById('spill-photo-input').click()}
              >
                <Camera className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {lang === 'fr' ? 'Oui' : 'Yes'}
              </button>
              <input
                id="spill-photo-input"
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  await handlePhotoCapture(e);
                  setStep('details');
                }}
                className="hidden"
                data-testid="photo-prompt-input"
              />
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Step: Details (text + photo)
  if (step === 'details') {
    return (
      <BrandingShell wallpaperUrl={brandingData.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-6 pb-8" data-testid="retailer-alert-details">
            <LogoDisplay />
            <div className="text-center mb-4">
              <h1 className="text-lg font-bold text-white mb-1">
                {(() => {
                  const cat = categories.find(c => c.id === selectedCategory);
                  return cat ? (typeof cat.name === 'object' ? (cat.name[lang] || cat.name.en) : cat.name) : '';
                })()}
              </h1>
              <p className="text-sm text-white/50">
                {lang === 'fr' ? 'Ajouter des d\u00e9tails (optionnel)' : 'Add details (optional)'}
              </p>
            </div>

            <div className="space-y-4">
              <Textarea
                placeholder={lang === 'fr' ? 'D\u00e9crivez le probl\u00e8me...' : 'Describe the issue...'}
                value={textComment}
                onChange={(e) => setTextComment(e.target.value)}
                className="min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 focus:ring-white/20"
                data-testid="text-comment-input"
              />

              {photo ? (
                <div className="relative">
                  <img src={photo.dataUrl} alt="Attached" className="w-full h-40 object-cover rounded-lg" />
                  <button
                    onClick={() => setPhoto(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center p-4 border-2 border-dashed border-white/15 rounded-2xl cursor-pointer hover:border-white/30 transition-colors" data-testid="photo-upload-label">
                  <Camera className="w-5 h-5 text-white/40 mr-2" strokeWidth={1.5} />
                  <span className="text-sm text-white/50">
                    {lang === 'fr' ? 'Prendre ou ajouter une photo' : 'Take or add a photo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoCapture}
                    className="hidden"
                    data-testid="photo-input"
                  />
                </label>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setStep(selectedCategory === 'spill_hazard' ? 'photo_prompt' : 'categories')}
                className="flex-1 h-12 rounded-2xl bg-white/5 border border-white/10 text-white/70 font-medium hover:bg-white/10 transition-all active:scale-[0.98]"
                data-testid="back-btn"
              >
                {lang === 'fr' ? 'Retour' : 'Back'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 h-12 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 font-medium hover:bg-green-500/25 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />{lang === 'fr' ? 'Envoi...' : 'Sending...'}</>
                ) : (
                  lang === 'fr' ? 'Envoyer le rapport' : 'Submit Report'
                )}
              </button>
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  return null;
}
